// Several assignees on one task: the chosen people as removable chips plus an
// "Add person" select listing the teammates not chosen yet.
import { X } from 'lucide-react'
import { Select } from '../../components/ui/select'
import { useT } from '../../i18n'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import type { TaskPerson } from './api'

type Props = {
  id?: string
  people: TaskPerson[]
  value: string[]
  onChange: (ids: string[]) => void
  disabled?: boolean
  /** Placeholder of the "add" select (defaults to the assignee wording). */
  addLabel?: string
}

export function AssigneePicker({ id, people, value, onChange, disabled, addLabel }: Props) {
  const t = useT(staffTasksI18n)
  const byId = new Map(people.map((person) => [person.user_id, person]))
  const rest = people.filter((person) => !value.includes(person.user_id))

  return (
    <div className="flex flex-col gap-2" data-testid="staff-task-assignees">
      {value.length > 0 && (
        <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
          {value.map((userId) => {
            const name = byId.get(userId)?.display_name ?? '—'
            return (
              <li
                key={userId}
                className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-surface-muted)] py-0.5 pl-2.5 pr-1 text-xs text-[var(--color-text)]"
              >
                {name}
                <button
                  type="button"
                  disabled={disabled}
                  aria-label={t.structure.removeAssignee(name)}
                  onClick={() => onChange(value.filter((other) => other !== userId))}
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[var(--color-text-muted)] hover:bg-[var(--color-border)] hover:text-[var(--color-text)] disabled:opacity-50"
                >
                  <X className="h-3 w-3" aria-hidden />
                </button>
              </li>
            )
          })}
        </ul>
      )}
      {rest.length > 0 && (
        <Select
          id={id}
          value=""
          disabled={disabled}
          aria-label={addLabel ?? t.structure.addAssignee}
          onChange={(e) => {
            if (e.target.value) onChange([...value, e.target.value])
          }}
        >
          <option value="">
            {addLabel ?? (value.length === 0 ? t.unassigned : t.structure.addAssignee)}
          </option>
          {rest.map((person) => (
            <option key={person.user_id} value={person.user_id}>
              {person.display_name}
            </option>
          ))}
        </Select>
      )}
    </div>
  )
}
